<template>
  <v-dialog
    v-model="rule_templates_dialog"
    :retain-focus="false"
    content-class="pricing-strategy-rule-templates-dialog"
  >
    <v-card
      v-if="rule_templates_dialog"
      class="d-flex flex-column rounded-lg h-100"
    >
      <!-- Header -->
      <v-card-title class="flex-shrink-0 d-flex align-center">
        <v-avatar size="40" color="secondary" class="mr-3" rounded>
          <v-icon color="white" size="22">mdi-view-grid-plus-outline</v-icon>
        </v-avatar>
        <div class="flex-grow-1" style="min-width: 0;">
          <div class="text-h6 font-weight-bold line-height-1">
            {{
              $t(
                isGuardrail
                  ? "guardrail_templates_dialog_title"
                  : "rule_templates_dialog_title"
              )
            }}
          </div>
          <div class="text-body-2 grey--text text-truncate">
            {{
              $t(
                isGuardrail
                  ? "guardrail_templates_dialog_subtitle"
                  : "rule_templates_dialog_subtitle",
                { strategy: (strategy && strategy.name) || "" }
              )
            }}
          </div>
        </div>
        <v-btn icon small color="grey" @click="closeDialog">
          <v-icon size="22">mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider class="ma-0" />

      <!-- Body: sidebar | grid | preview -->
      <div class="dialog-body d-flex flex-grow-1">
        <!-- Sidebar de tags -->
        <aside v-if="!isGuardrail" class="sidebar pa-4 overflow-y-auto">
          <v-text-field
            v-model="search"
            :placeholder="$t('rule_templates_search')"
            prepend-inner-icon="mdi-magnify"
            rounded
            outlined
            dense
            clearable
            hide-details
            class="tags-search-field"
          />
          <div class="d-flex align-center justify-space-between mt-4 mb-2">
            <span
              class="text-caption font-weight-bold text-uppercase grey--text"
            >
              {{ $t("rule_templates_tags_title") }}
            </span>
            <v-btn
              v-if="active_tag_codes.length"
              x-small
              text
              rounded
              color="secondary"
              @click="active_tag_codes = []"
            >
              {{ $t("rule_templates_clear_tags") }}
            </v-btn>
          </div>
          <v-chip-group v-model="active_tag_codes" multiple column>
            <v-chip
              v-for="tag in tags"
              :key="tag.code"
              :value="tag.code"
              small
              :color="tagColor(tag.code).tint"
              :text-color="tagColor(tag.code).color"
              class="tag-chip w-100"
            >
              <span>{{ tagName(tag.code) }}</span>
              <span class="ml-1 text-caption">
                {{ tagCounts[tag.code] || 0 }}
              </span>
            </v-chip>
          </v-chip-group>
        </aside>

        <!-- Grid de plantillas y reglas -->
        <section
          class="templates-grid-wrapper flex-grow-1 pa-4 overflow-y-auto"
        >
          <v-btn-toggle
            v-model="source_filter"
            mandatory
            dense
            rounded
            class="mb-4"
          >
            <v-btn small text value="all">
              {{ $t("rule_templates_filter_all") }}
            </v-btn>
            <v-btn small text value="templates">
              {{ $t("rule_templates_filter_templates") }}
            </v-btn>
            <v-btn small text value="rules">
              {{ $t("rule_templates_filter_my_rules") }}
            </v-btn>
          </v-btn-toggle>

          <div v-if="filteredItems.length" class="templates-grid">
            <v-card
              v-for="item in filteredItems"
              :key="item.key"
              outlined
              class="template-card d-flex flex-column pa-3 rounded-lg"
              :class="{
                selected: item.key === selected_key,
                blank: item.kind === 'blank'
              }"
              @click="selected_key = item.key"
              @dblclick.native="onCardDblClick(item)"
            >
              <div class="d-flex align-center justify-space-between">
                <span class="d-flex align-center" style="min-width: 0;">
                  <v-avatar
                    size="26"
                    :color="kindColorSet(item).tint"
                    class="mr-2 flex-shrink-0"
                  >
                    <v-icon size="15" :color="kindColorSet(item).color">
                      {{ kindIcon(item) }}
                    </v-icon>
                  </v-avatar>
                  <span class="font-weight-bold text-body-2 text-truncate">
                    {{ item.name }}
                  </span>
                </span>
                <v-icon
                  v-if="item.key === selected_key"
                  color="secondary"
                  size="20"
                >
                  mdi-check-circle
                </v-icon>
              </div>
              <div class="text-body-2 grey--text description">
                {{ item.description }}
              </div>
              <div
                v-if="item.kind === 'rule'"
                class="text-caption grey--text d-flex align-center"
              >
                <v-icon size="14" color="grey" class="mr-1">
                  mdi-chart-timeline-variant
                </v-icon>
                {{ usagePhrase(item.strategies_count) }}
              </div>
              <div
                v-if="!isGuardrail && item.tag_codes.length"
                class="d-flex flex-wrap"
                style="gap: 4px;"
              >
                <v-chip
                  v-for="code in item.tag_codes"
                  :key="code"
                  x-small
                  :color="tagColor(code).tint"
                  :text-color="tagColor(code).color"
                  class="font-weight-medium"
                >
                  {{ tagName(code) }}
                </v-chip>
              </div>
            </v-card>
          </div>
          <div
            v-else
            class="flex-grow-1 d-flex flex-column align-center justify-center text-center py-10 px-5"
          >
            <v-icon size="48" color="grey lighten-1">
              mdi-magnify-remove-outline
            </v-icon>
            <div class="text-body-1 grey--text mt-2">
              {{ $t("rule_templates_empty") }}
            </div>
          </div>
        </section>

        <!-- Preview -->
        <aside class="preview-panel d-flex flex-column pa-5 overflow-y-auto">
          <template v-if="selectedItem && selectedItem.kind !== 'blank'">
            <div
              v-if="!isGuardrail && selectedItem.tag_codes.length"
              class="d-flex flex-wrap mb-2"
              style="gap: 4px;"
            >
              <v-chip
                v-for="code in selectedItem.tag_codes"
                :key="code"
                x-small
                :color="tagColor(code).tint"
                :text-color="tagColor(code).color"
                class="font-weight-medium"
              >
                {{ tagName(code) }}
              </v-chip>
            </div>
            <div class="d-flex align-center justify-space-between">
              <div class="text-h6 font-weight-bold line-height-1">
                {{ selectedItem.name }}
              </div>
              <v-btn
                v-if="selectedItem.kind === 'rule'"
                icon
                small
                color="grey"
                class="flex-shrink-0 ml-2"
                @click="show_delete_dialog = true"
              >
                <v-icon size="20">mdi-delete-outline</v-icon>
              </v-btn>
            </div>
            <div class="text-body-2 grey--text mt-2">
              {{ selectedItem.description }}
            </div>
            <div
              v-if="selectedItem.kind === 'rule'"
              class="text-caption grey--text d-flex align-center mt-1"
            >
              <v-icon size="14" color="grey" class="mr-1">
                mdi-chart-timeline-variant
              </v-icon>
              {{ usagePhrase(selectedItem.strategies_count) }}
            </div>

            <a-pricing-strategy-rule-detail
              :key="selected_key"
              :rule="selectedItem.rule"
              :graph-color="
                selectedItem.kind === 'template'
                  ? tagColor(primaryTagCode).color
                  : null
              "
              :graph-tint="
                selectedItem.kind === 'template'
                  ? tagColor(primaryTagCode).tint
                  : null
              "
              class="mt-3"
            />
          </template>
          <div
            v-else-if="selectedItem && selectedItem.kind === 'blank'"
            class="flex-grow-1 d-flex flex-column align-center justify-center text-center py-10 px-5"
          >
            <v-icon size="44" color="grey lighten-1">
              mdi-pencil-plus-outline
            </v-icon>
            <div class="text-h6 font-weight-bold mt-2">
              {{ $t("rule_templates_blank_title") }}
            </div>
            <div class="text-body-2 grey--text mt-2">
              {{ $t("rule_templates_blank_desc") }}
            </div>
          </div>
          <div
            v-else
            class="flex-grow-1 d-flex flex-column align-center justify-center text-center py-10 px-5"
          >
            <v-icon size="44" color="grey lighten-1">mdi-gesture-tap</v-icon>
            <div class="text-body-2 grey--text mt-2">
              {{ $t("rule_templates_preview_placeholder") }}
            </div>
          </div>
        </aside>
      </div>

      <v-divider class="ma-0" />
      <!-- Footer -->
      <v-card-actions class="flex-shrink-0 px-4 py-3">
        <v-btn text rounded depressed @click="closeDialog">
          {{ $t("cancel") }}
        </v-btn>
        <v-spacer />
        <v-btn
          v-if="selectedItem && selectedItem.kind === 'rule'"
          text
          rounded
          color="secondary"
          :loading="cloning"
          @click="cloneSelected"
        >
          <v-icon left size="18">mdi-content-copy</v-icon>
          {{ $t("clone") }}
        </v-btn>
        <v-btn
          text
          rounded
          color="secondary"
          :disabled="!selectedItem"
          @click="configureSelected"
        >
          <v-icon left size="18">mdi-pencil-outline</v-icon>
          {{ $t("configure") }}
        </v-btn>
        <v-btn
          color="secondary"
          rounded
          class="pl-5 pr-5"
          :disabled="!selectedItem || selectedItem.kind === 'blank'"
          :loading="placing"
          @click="useSelected"
        >
          <v-icon left size="18">mdi-arrow-right</v-icon>
          {{ primaryActionLabel }}
        </v-btn>
      </v-card-actions>

      <!-- Confirmación de borrado de una de Mis reglas -->
      <v-dialog
        v-model="show_delete_dialog"
        :fullscreen="$vuetify.breakpoint.xsOnly"
        max-width="400"
      >
        <v-card v-if="selectedItem && selectedItem.kind === 'rule'">
          <v-card-title center class="headline justify-center">
            {{ $t("are_you_sure") }}
          </v-card-title>
          <v-card-text class="text-center justify-center">
            <p>{{ $t("sure_delete_pricing_strategy_rule") }}</p>
            <div class="font-weight-bold">{{ selectedItem.name }}</div>
            <div
              v-if="selectedItem.strategies_count > 0"
              v-html="
                $t('sure_delete_pricing_strategy_rule_count', {
                  count: selectedItem.strategies_count
                })
              "
              class="font-weight-medium mt-4"
            ></div>
          </v-card-text>
          <v-card-actions class="ps-5 pb-5 justify-center">
            <v-btn
              color="red"
              rounded
              depressed
              text
              class="pl-5 pr-5"
              :loading="deleting"
              @click="deleteSelected"
            >
              {{ $t("delete") }}
            </v-btn>
            <v-btn
              text
              rounded
              depressed
              class="pl-5 pr-5"
              @click="show_delete_dialog = false"
            >
              {{ $t("cancel") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import aPricingStrategyRuleDetail from "@/components/pricing-strategy/aPricingStrategyRuleDetail.vue";
import {
  tagColorSet,
  randomRuleColor,
  ruleSummaryPhrase
} from "@/components/pricing-strategy/pricingStrategyUtils";
import { mapGetters, mapState } from "vuex";
import * as Utils from "@/utils.js";

export default {
  name: "a-pricing-strategy-rule-templates-dialog",
  components: {
    aPricingStrategyRuleDetail
  },
  data: () => ({
    search: "",
    active_tag_codes: [],
    selected_key: null,
    source_filter: "all",
    placing: false,
    cloning: false,
    show_delete_dialog: false,
    deleting: false
  }),
  computed: {
    rule_templates_dialog: {
      get() {
        return this.$store.state.pricingstrategy.rule_templates_dialog;
      },
      set(value) {
        this.$store.dispatch("setPricingStrategyRuleTemplatesDialog", value);
      }
    },
    rule_to_edit: {
      get() {
        return this.$store.state.pricingstrategy.rule_to_edit;
      },
      set(value) {
        this.$store.dispatch("setPricingStrategyRuleToEdit", value);
      }
    },
    rule_definition_dialog: {
      get() {
        return this.$store.state.pricingstrategy.rule_definition_dialog;
      },
      set(value) {
        this.$store.dispatch("setPricingStrategyRuleDefinitionDialog", value);
      }
    },
    ...mapState({
      rule_templates: state => state.pricingstrategy.rule_templates,
      guardrail_templates: state => state.pricingstrategy.guardrail_templates,
      rules: state => state.pricingstrategy.rules,
      guardrails: state => state.pricingstrategy.guardrails,
      rule_templates_kind: state => state.pricingstrategy.rule_templates_kind,
      strategy: state => state.pricingstrategy.strategy,
      strategy_room_types: state => state.pricingstrategy.strategy_room_types,
      rule_types: state => state.pricingstrategy.rule_types,
      chain: state => state.chain.chain
    }),
    ...mapGetters({
      current_hotel: "current_hotel"
    }),
    // El catálogo se abre en modo regla o en modo guardarail. Cada uno tiene
    // su propio dataset, así que aquí solo se elige la fuente; el resto del
    // diálogo es el mismo, salvo el sidebar, que en guardarailes se oculta.
    isGuardrail() {
      return this.rule_templates_kind === "guardrail";
    },
    templatesSource() {
      return this.isGuardrail ? this.guardrail_templates : this.rule_templates;
    },
    rulesSource() {
      return this.isGuardrail ? this.guardrails : this.rules;
    },
    tags() {
      return (this.templatesSource && this.templatesSource.tags) || [];
    },
    blankItem() {
      return {
        key: "blank",
        kind: "blank",
        name: this.$t("rule_templates_blank_title"),
        description: this.$t("rule_templates_blank_desc"),
        tag_codes: []
      };
    },
    templateItems() {
      const raw =
        (this.templatesSource && this.templatesSource.templates) || [];
      return raw.map(t => ({
        key: `template:${t.code}`,
        kind: "template",
        name: t.name,
        description: t.description,
        tag_codes: t.tag_codes || [],
        rule: t.rule
      }));
    },
    myRuleItems() {
      return (this.rulesSource || []).map(r => ({
        key: `rule:${r.id}`,
        kind: "rule",
        name: r.name,
        // Compuesta en cliente: el `resume` del backend son cadenas de su
        // modelo interno ("notify P2", "high") y se leían fatal aquí.
        description: ruleSummaryPhrase(this, r),
        tag_codes: r.tag_codes || [],
        rule: r,
        strategies_count: r.strategies_count || 0
      }));
    },
    // Plantillas/reglas que pasan la búsqueda (base de los contadores por tag)
    searchedTemplates() {
      return this.templateItems.filter(i => this.matchesSearch(i));
    },
    searchedRules() {
      return this.myRuleItems.filter(i => this.matchesSearch(i));
    },
    filteredItems() {
      const hasFilters =
        (this.search || "").trim() || this.active_tag_codes.length;
      const templates = this.searchedTemplates.filter(i => this.matchesTags(i));
      const myRules = this.searchedRules.filter(i => this.matchesTags(i));
      const items = [];
      if (this.source_filter !== "rules" && !hasFilters) {
        items.push(this.blankItem);
      }
      if (this.source_filter !== "rules") items.push(...templates);
      if (this.source_filter !== "templates") items.push(...myRules);
      return items;
    },
    // Contadores por tag sobre lo visible según el filtro de fuente,
    // ya filtrado por búsqueda (las reglas también pueden traer tags)
    tagCounts() {
      const counts = {};
      const pool = [
        ...(this.source_filter !== "rules" ? this.searchedTemplates : []),
        ...(this.source_filter !== "templates" ? this.searchedRules : [])
      ];
      pool.forEach(t =>
        t.tag_codes.forEach(c => {
          counts[c] = (counts[c] || 0) + 1;
        })
      );
      return counts;
    },
    selectedItem() {
      return this.filteredItems.find(i => i.key === this.selected_key) || null;
    },
    primaryTagCode() {
      return this.selectedItem && this.selectedItem.tag_codes[0];
    },
    primaryActionLabel() {
      return this.selectedItem && this.selectedItem.kind === "rule"
        ? this.$t("use_rule")
        : this.$t("use_template");
    }
  },
  watch: {
    rule_templates_dialog(open) {
      if (open) {
        this.search = "";
        this.active_tag_codes = [];
        this.selected_key = null;
        this.source_filter = "all";
        this.placing = false;
        this.cloning = false;
        this.show_delete_dialog = false;
        this.deleting = false;
      }
    }
  },
  methods: {
    matchesSearch(item) {
      const q = (this.search || "").toLowerCase().trim();
      if (!q) return true;
      return (
        (item.name || "").toLowerCase().includes(q) ||
        (item.description || "").toLowerCase().includes(q)
      );
    },
    // Filtro por tags (OR entre tags activos); sin tags → se oculta
    matchesTags(item) {
      if (!this.active_tag_codes.length) return true;
      return item.tag_codes.some(c => this.active_tag_codes.includes(c));
    },
    kindIcon(item) {
      return {
        blank: "mdi-pencil-plus-outline",
        template: "mdi-view-grid-outline",
        rule: "mdi-account-outline"
      }[item.kind];
    },
    // Avatar del tipo de card: color base + tinte de fondo derivado
    kindColorSet(item) {
      const base = {
        blank: "#78909C",
        template: "#2E21BD",
        rule: "#00916E"
      }[item.kind];
      return tagColorSet(base);
    },
    usagePhrase(count) {
      if (!count) return this.$t("rule_used_in_strategies_zero");
      if (count === 1) return this.$t("rule_used_in_strategies_one");
      return this.$t("rule_used_in_strategies_other", { count });
    },
    // Color del tag desde el endpoint (+ tinte derivado); fallback neutro
    tagColor(code) {
      const tag = this.tags.find(t => t.code === code);
      return tagColorSet(tag && tag.color);
    },
    tagName(code) {
      const tag = this.tags.find(t => t.code === code);
      return (tag && tag.name) || code;
    },
    closeDialog() {
      this.rule_templates_dialog = false;
    },
    // Semilla común a "usar plantilla" y "plantilla en blanco"
    baseSeed() {
      return {
        hotel_id: this.current_hotel.id,
        rule_kind: this.isGuardrail ? "guardrail" : "rule",
        room_type_ids:
          this.strategy_room_types && this.strategy_room_types.length === 1
            ? [this.strategy_room_types[0].id]
            : [],
        color: randomRuleColor(this.chain && this.chain.colors)
      };
    },
    // Siguiente posición libre en el orden de evaluación (el fijo no cuenta)
    nextGuardrailPosition() {
      const current = (this.strategy && this.strategy.strategyGuardrails) || [];
      return current.filter(g => !g.locked).length;
    },
    // Un guardarail no se coloca en el grid: se engancha al final de la
    // estrategia, porque ocupa toda la antelación.
    attachGuardrail(ruleId) {
      return this.$store.dispatch("setPricingStrategyStrategyGuardrail", {
        strategy_id: this.strategy.id,
        hotel_id: this.current_hotel.id,
        rule_id: ruleId,
        position: this.nextGuardrailPosition()
      });
    },
    // Botón primario: ir directamente a colocar la regla en el grid
    useSelected() {
      const item = this.selectedItem;
      if (!item || item.kind === "blank") return;
      if (item.kind === "rule") {
        if (this.isGuardrail) {
          this.placing = true;
          this.attachGuardrail(item.rule.id)
            .then(() => {
              this.$store.dispatch("setSnackbar", {
                title: this.$t("guardrail_added"),
                color: "accent"
              });
              this.closeDialog();
            })
            .catch(() => {
              Utils.showError(this, "guardrail_not_added");
            })
            .finally(() => {
              this.placing = false;
            });
          return;
        }
        this.$store.dispatch("setPricingStrategyRuleToPlace", item.rule);
        this.closeDialog();
        return;
      }
      // Plantilla: crear la regla sin pasar por el canvas y colocarla
      // Deep clone: el canvas/store mutan settings por referencia; sin clonar
      // corromperíamos la fixture del mock a nivel de módulo.
      const rule = JSON.parse(JSON.stringify(item.rule));
      this.placing = true;
      this.$store.dispatch("setSnackbar", {
        title: this.$t(this.isGuardrail ? "adding_guardrail" : "adding_rule"),
        color: "secondary"
      });
      this.$store
        .dispatch("addPricingStrategyRule", {
          ...this.baseSeed(),
          name: item.name,
          schedule: rule.schedule,
          routes: rule.routes,
          action: rule.action
        })
        .then(response => {
          if (this.isGuardrail) {
            return this.attachGuardrail(response.data.id).then(() => {
              this.$store.dispatch("setSnackbar", {
                title: this.$t("guardrail_added"),
                color: "accent"
              });
              this.closeDialog();
            });
          }
          this.$store.dispatch("setSnackbar", {
            title: this.$t("rule_added"),
            color: "accent"
          });
          this.$store.dispatch("setPricingStrategyRuleToPlace", response.data);
          this.closeDialog();
        })
        .catch(() => {
          Utils.showError(
            this,
            this.isGuardrail ? "guardrail_not_added" : "rule_not_added"
          );
        })
        .finally(() => {
          this.placing = false;
        });
    },
    // Abrir el canvas: en blanco, con la plantilla (sin id → create) o con la
    // regla original (con id → edit)
    configureSelected() {
      const item = this.selectedItem;
      if (!item) return;
      if (item.kind === "blank") {
        const availableTypes = (this.rule_types || []).filter(t =>
          this.isGuardrail ? t.allowed_in_guardrails === true : true
        );
        this.rule_to_edit = {
          ...this.baseSeed(),
          rule_type_code:
            availableTypes.length === 1 ? availableTypes[0].code : null
        };
      } else if (item.kind === "template") {
        const rule = JSON.parse(JSON.stringify(item.rule));
        this.rule_to_edit = {
          ...this.baseSeed(),
          name: item.name,
          schedule: rule.schedule,
          routes: rule.routes,
          action: rule.action
        };
      } else {
        this.rule_to_edit = item.rule;
      }
      this.$store.dispatch(
        "setPricingStrategyRuleDefinitionMode",
        this.isGuardrail ? "guardrail" : "rule"
      );
      this.rule_templates_dialog = false;
      this.rule_definition_dialog = true;
    },
    // Eliminar la regla seleccionada (del catálogo y de las estrategias que
    // la usan)
    deleteSelected() {
      const item = this.selectedItem;
      if (!item || item.kind !== "rule") return;
      this.deleting = true;
      this.$store.dispatch("setSnackbar", {
        title: this.$t("deleting_rule"),
        color: "secondary"
      });
      this.$store
        .dispatch("deletePricingStrategyRule", {
          rule_id: item.rule.id,
          rule_kind: item.rule.rule_kind
        })
        .then(() => {
          this.$store.dispatch("setSnackbar", {
            title: this.$t("rule_deleted"),
            color: "accent"
          });
          this.selected_key = null;
        })
        .catch(() => {
          Utils.showError(this, "rule_not_deleted");
        })
        .finally(() => {
          this.deleting = false;
          this.show_delete_dialog = false;
        });
    },
    // Duplicar la regla seleccionada y abrir el canvas con el clon
    cloneSelected() {
      const item = this.selectedItem;
      if (!item || item.kind !== "rule") return;
      this.cloning = true;
      this.$store
        .dispatch("duplicatePricingStrategyRule", {
          rule_id: item.rule.id,
          rule_kind: item.rule.rule_kind
        })
        .then(response => {
          this.$store.dispatch(
            "setPricingStrategyRuleDefinitionMode",
            this.isGuardrail ? "guardrail" : "rule"
          );
          this.rule_to_edit = response.data;
          this.rule_templates_dialog = false;
          this.rule_definition_dialog = true;
        })
        .catch(() => {
          Utils.showError(this, "rule_not_added");
        })
        .finally(() => {
          this.cloning = false;
        });
    },
    onCardDblClick(item) {
      // El primer click del dblclick ya ha seleccionado la card
      if (item.kind === "blank") {
        this.configureSelected();
      } else {
        this.useSelected();
      }
    }
  }
};
</script>

<style lang="scss">
.pricing-strategy-rule-templates-dialog {
  height: 98vh !important;
  width: 98vw !important;
  margin: auto !important;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  // Solo lo que Vuetify 2.3.18 no cubre con clases: anchos de columna,
  // bordes, gap, grid, estados custom y tamaños fuera de la escala de 4px.

  .dialog-body {
    min-height: 0; // clave: sin esto las columnas flex no scrollean

    .sidebar {
      flex: 0 0 232px;
      border-right: 1px solid rgba(0, 0, 0, 0.08);
    }

    .templates-grid-wrapper {
      min-width: 0;
      background: #fcfdfe;
    }

    .templates-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;
      align-content: start;
    }

    .preview-panel {
      flex: 0 0 340px;
      border-left: 1px solid rgba(0, 0, 0, 0.08);
    }
  }

  .tags-search-field {
    .v-input__slot {
      min-height: auto !important;
      padding: 0 6px !important;
    }
    .v-input__prepend-inner,
    .v-input__append-inner {
      margin-top: 5px !important;
      .v-input__icon {
        .v-icon {
          font-size: 20px !important;
        }
      }
    }
    .input {
      padding: 4px 0 !important;
    }
  }

  // v-chip del sidebar: deseleccionado atenuado, activo a color pleno
  .tag-chip {
    font-weight: 500;
    margin-left: 1px;
    opacity: 0.55;
    transition: opacity 0.15s ease;

    &:hover {
      opacity: 0.85;
    }
    &.v-chip--active {
      font-weight: 700;
      opacity: 1;
      box-shadow: 0 0 0 1px currentColor;
    }
    .v-chip__content {
      justify-content: space-between;
      width: 100%;
    }
    .v-icon {
      font-size: 16px;
    }
  }

  .template-card {
    gap: 8px;
    transition: box-shadow 0.15s ease, border-color 0.15s ease;

    &:hover {
      box-shadow: 0 3px 12px rgba(38, 50, 56, 0.12);
    }
    // Selección con box-shadow (no engordar el border → evita reflow y las
    // trampas de border-color con los resets de Vuetify)
    &.selected {
      border-color: var(--v-secondary-base);
      box-shadow: 0 0 0 1px var(--v-secondary-base);
      background: rgba(46, 33, 189, 0.04);
    }
    &.blank {
      border-style: dashed;
    }

    .description {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }
}

// xs (fullscreen): apilar columnas
@media (max-width: 599px) {
  .pricing-strategy-rule-templates-dialog {
    .dialog-body {
      flex-direction: column;
      overflow-y: auto;

      .sidebar,
      .preview-panel {
        flex: 0 0 auto;
        width: 100%;
        border: none;
        border-bottom: 1px solid rgba(0, 0, 0, 0.08);
      }
      .templates-grid {
        grid-template-columns: 1fr;
      }
    }
  }
}
</style>
